import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Save, User, Phone, Tag, Calendar, ArrowLeft } from "lucide-react";
import styled from "styled-components";
import { theme } from "../styles/theme";
import { CreateLeadRequest, Lead } from "../types/api";
import { useCreateLead, useUpdateLead } from "../hooks/useApi";
import FadeInContent from "../components/FadeInContent";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - 120px); // Adjust this value based on header height
  padding: ${props => props.theme.spacing[6]};
  background-color: ${props => props.theme.colors.background};
`;

const PageHeader = styled.div`
  width: 100%;
  max-width: 760px;
  margin-bottom: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const BackButton = styled.button`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  padding: ${props => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
  width: 100%;
  max-width: 760px;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const CardTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const CardBody = styled.div`
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const TallCardBody = styled(CardBody)`
  min-height: clamp(240px, 34vh, 360px);
  justify-content: flex-start;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    min-height: clamp(220px, 30vh, 320px);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    min-height: auto;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[4]};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const TallFormGroup = styled(FormGroup)`
  min-height: clamp(180px, 30vh, 260px);
  justify-content: flex-start;
  gap: ${props => props.theme.spacing[4]};
  padding-bottom: ${props => props.theme.spacing[2]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    min-height: clamp(160px, 26vh, 220px);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    min-height: auto;
  }
`;

const Label = styled.label<{ $required?: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};

  ${(props) =>
    props.$required &&
    `
    &::after {
      content: ' *';
      color: ${props.theme.colors.error};
    }
  `}
`;

const Input = styled.input<{ $error?: boolean }>`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$error ? props.theme.colors.error : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
    outline: none;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.error};
  margin-top: ${props => props.theme.spacing[1]};
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing[3]};
  width: 100%;
  max-width: 760px;
  margin-top: ${props => props.theme.spacing[2]};
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;

  ${(props) => {
    switch (props.$variant) {
      case "primary":
        return `
          background: ${props.theme.colors.primary};
          color: ${props.theme.colors.textPrimary};
          &:hover {
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadows.neon};
            background: ${props.theme.colors.primaryHover};
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundSecondary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundTertiary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const statusOptions = [
  { value: "novo", label: "Novo" },
  { value: "qualificado", label: "Qualificado" },
  { value: "prospect", label: "Prospect" },
  { value: "convertido", label: "Convertido" },
  { value: "perdido", label: "Perdido" },
];

const DropdownContainer = styled.div`
  position: relative;
`;
const DropdownButton = styled.button<{ $error?: boolean }>`
  width: 100%;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid
    ${(props) => (props.$error ? props.theme.colors.error : props.theme.colors.border)};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  text-align: left;

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
  }
`;
const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  box-shadow: ${props => props.theme.shadows.md};
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: 10;
  max-height: 220px;
  overflow-y: auto;
`;
const DropdownItem = styled.li`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  color: ${props => props.theme.colors.textTertiary};
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${props => props.theme.colors.backgroundSecondary};
  }
`;

const NewLeadPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // Detecta se está no modo de edição
  const editingLead = location.state?.lead as Lead | undefined;
  const isEditMode = !!editingLead && !!id;

  const createLeadMutation = useCreateLead();
  const updateLeadMutation = useUpdateLead();

  const [formData, setFormData] = useState<CreateLeadRequest>({
    nome: editingLead?.nome || "",
    telefone: editingLead?.telefone || "",
    status: editingLead?.status || "novo",
    interesse: editingLead?.interesse || "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownOpenInterests, setDropdownOpenInterests] = useState(false);
  const [intents, setIntents] = useState<string[]>([]);
  const [loadingIntents, setLoadingIntents] = useState(false);
  const interestsRef = useRef<HTMLDivElement | null>(null);
  const nomeRef = useRef<HTMLInputElement | null>(null);
  const telefoneRef = useRef<HTMLInputElement | null>(null);
  const statusButtonRef = useRef<HTMLButtonElement | null>(null);
  const interesseButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (interestsRef.current && !interestsRef.current.contains(target)) {
        setDropdownOpenInterests(false);
      }
    };

    if (dropdownOpenInterests) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpenInterests]);

  useEffect(() => {
    let mounted = true;
    const fetchIntents = async () => {
      setLoadingIntents(true);
      try {
        const res = await (
          await import("../services/api")
        ).leadsApi.getStats();
        console.debug("leadsApi.getStats result:", res);

        const normalizeToArray = (input: unknown): string[] => {
          if (!input) return [];
          if (Array.isArray(input)) return input.map((i) => String(i));
          if (typeof input === "object") {
            const obj = input as Record<string, unknown>;
            const vals = Object.values(obj)
              .filter((v) => v !== null && v !== undefined)
              .map((v) => String(v as unknown));
            if (vals.length > 0) return vals;
          }
          const asStr = String(input);
          if (asStr.includes(","))
            return asStr
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
          return [asStr];
        };

        let items: string[] = [];

        if (res && typeof res === "object") {
          const r = res as unknown as Record<string, unknown>;
          const dataField = r["data"];
          const interestsField =
            dataField && typeof dataField === "object"
              ? (dataField as Record<string, unknown>)[
                  "interesses_reconhecidos"
                ]
              : r["interesses_reconhecidos"];

          if (interestsField) items = normalizeToArray(interestsField);
          else if (Array.isArray(dataField))
            items = normalizeToArray(dataField);
          else items = normalizeToArray(r);
        }

        const withGeral = Array.from(
          new Set([...items.map((i) => i.trim()).filter(Boolean), "Geral"])
        );
        const upper = withGeral.map((i) => String(i).toUpperCase());
        if (mounted) setIntents(Array.from(new Set(upper)).sort());
      } catch (e) {
        console.error("Erro ao buscar interesses de leads:", e);
      } finally {
        if (mounted) setLoadingIntents(false);
      }
    };

    fetchIntents();

    return () => {
      mounted = false;
    };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = "Nome é obrigatório";
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = "Telefone é obrigatório";
    } else if (!/^\+\d{2}\s\(\d{2}\)\s\d{4,5}-\d{4}$/.test(formData.telefone)) {
      newErrors.telefone = "Formato inválido. Use +55 (XX) 9XXXX-XXXX ou XXXX-XXXX";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode && editingLead) {
        // Modo de edição
        await updateLeadMutation.mutateAsync({
          id: editingLead.id,
          data: formData,
        });
      } else {
        // Modo de criação
        await createLeadMutation.mutateAsync(formData);
      }
      navigate("/leads");
    } catch (error) {
      console.error(`Erro ao ${isEditMode ? 'atualizar' : 'criar'} lead:`, error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    const ddi = numbers.slice(0, 2);
    const ddd = numbers.slice(2, 4);
    
    if (numbers.length <= 2) return `+${numbers}`;
    if (numbers.length <= 4) return `+${ddi} (${ddd}`;
    
    if (numbers.length <= 8) {
      const part1 = numbers.slice(4, 8);
      return `+${ddi} (${ddd}) ${part1}`;
    } else if (numbers.length <= 9) {
      const part1 = numbers.slice(4, 9);
      return `+${ddi} (${ddd}) ${part1}`;
    } else if (numbers.length <= 12) {
      const part1 = numbers.slice(4, 8);
      const part2 = numbers.slice(8, 12);
      return `+${ddi} (${ddd}) ${part1}-${part2}`;
    } else {
      const part1 = numbers.slice(4, 9);
      const part2 = numbers.slice(9, 13);
      return `+${ddi} (${ddd}) ${part1}-${part2}`;
    }
  };

  return (
    <FadeInContent>
      <PageContainer>
        <PageHeader>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </BackButton>
          <PageTitle>{isEditMode ? 'Editar Lead' : 'Criar Novo Lead'}</PageTitle>
        </PageHeader>

        <Form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <User size={20} />
              <CardTitle>Informações Pessoais</CardTitle>
            </CardHeader>
            <CardBody>
              <FormRow>
                <FormGroup>
                  <Label $required>Nome Completo</Label>
                  <Input
                    type="text"
                    ref={nomeRef}
                    value={formData.nome}
                    onChange={(e) => handleInputChange("nome", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        telefoneRef.current?.focus();
                      }
                    }}
                    placeholder="Digite o nome completo"
                    $error={!!errors.nome}
                  />
                  {errors.nome && <ErrorMessage>{errors.nome}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label $required>Telefone</Label>
                  <Input
                    type="text"
                    ref={telefoneRef}
                    value={formData.telefone}
                    onChange={(e) =>
                      handleInputChange("telefone", formatPhone(e.target.value))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        statusButtonRef.current?.focus();
                        setDropdownOpen(true);
                      }
                    }}
                    placeholder="+55 (51) 99999-9999"
                    $error={!!errors.telefone}
                  />
                  {errors.telefone && (
                    <ErrorMessage>{errors.telefone}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Tag size={20} />
              <CardTitle>Status e Interesse</CardTitle>
            </CardHeader>
            <TallCardBody>
              <FormRow>
                <TallFormGroup>
                  <Label>Status</Label>
                  <DropdownContainer>
                    <DropdownButton
                      type="button"
                      ref={statusButtonRef}
                      onClick={() => setDropdownOpen((open) => !open)}
                      $error={!!errors.status}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (dropdownOpen) {
                            const first = statusOptions[0];
                            if (first) {
                              handleInputChange("status", first.value);
                            }
                            setDropdownOpen(false);
                            interesseButtonRef.current?.focus();
                            setDropdownOpenInterests(true);
                          } else {
                            setDropdownOpen(true);
                          }
                        } else if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setDropdownOpen(true);
                        }
                      }}
                    >
                      {statusOptions.find((opt) => opt.value === formData.status)
                        ?.label || "Selecione"}
                    </DropdownButton>
                    {dropdownOpen && (
                      <DropdownList>
                        {statusOptions.map((opt) => (
                          <DropdownItem
                            key={opt.value}
                            onClick={() => {
                              handleInputChange("status", opt.value);
                              setDropdownOpen(false);
                            }}
                          >
                            {opt.label}
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    )}
                  </DropdownContainer>
                </TallFormGroup>

                <TallFormGroup ref={interestsRef}>
                  <Label>Interesse</Label>
                  <DropdownContainer>
                    <DropdownButton
                      type="button"
                      ref={interesseButtonRef}
                      onClick={() => setDropdownOpenInterests((open) => !open)}
                      $error={!!errors.interesse}
                      disabled={loadingIntents}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (dropdownOpenInterests) {
                            const first = intents[0];
                            if (first) handleInputChange("interesse", first);
                            setDropdownOpenInterests(false);
                            handleSubmit(
                              new Event("submit") as unknown as React.FormEvent
                            );
                          } else {
                            setDropdownOpenInterests(true);
                          }
                        } else if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setDropdownOpenInterests(true);
                        }
                      }}
                    >
                      {formData.interesse ||
                        (loadingIntents
                          ? "Carregando..."
                          : "Selecione um interesse")}
                    </DropdownButton>

                    {dropdownOpenInterests && intents.length > 0 && (
                      <DropdownList>
                        {intents.map((it) => (
                          <DropdownItem
                            key={it}
                            onClick={() => {
                              handleInputChange("interesse", it);
                              setDropdownOpenInterests(false);
                            }}
                          >
                            {it}
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    )}
                  </DropdownContainer>
                </TallFormGroup>
              </FormRow>
            </TallCardBody>
          </Card>
          
          <FormFooter>
            <Button type="button" onClick={() => navigate("/leads")}>
              Cancelar
            </Button>
            <Button
              type="submit"
              $variant="primary"
              onClick={handleSubmit}
              disabled={createLeadMutation.isPending || updateLeadMutation.isPending}
            >
              <Save size={16} />
              {(createLeadMutation.isPending || updateLeadMutation.isPending)
                ? "Salvando..."
                : isEditMode
                  ? "Salvar Alterações"
                  : "Criar Lead"}
            </Button>
          </FormFooter>
        </Form>
      </PageContainer>
    </FadeInContent>
  );
};

export default NewLeadPage;
